import {
    Box,
    Flex,
    HStack,
    VStack,
    Text,
    Icon,
    Button,
    useDisclosure,
    Switch,
    Divider,
    Accordion,
    AccordionItem,
    AccordionButton,
    AccordionPanel,
    AccordionIcon,
    Badge,
} from '@chakra-ui/react';
import AppDrawer from '../../../components/AppDrawer';
import { useState, useEffect, useCallback } from 'react';
import type { ReactNode } from 'react';
import {
    TbLayoutList,
    TbListDetails,
    TbColumns,
    TbArrowBackUp,
} from 'react-icons/tb';
import { Icon as YaxleIcon, IconButton } from '../../../ui';
import type { IconButtonProps } from '../../../ui';
import type { TableSettings } from '../../../hooks/useTableSettings';
import SettingsColumnsPanel, {
    type SettingsColumnDrawerRow,
    reorderColumnRows,
} from './SettingsColumnsPanel';
import {
    cloneNestedRows,
    toggleNestedRow,
    type NestedColumnGroupDef,
} from '../nestedColumnGroups';

interface ReportingTableSettingsDrawerProps {
    settings: TableSettings;
    onUpdateSettings: (partial: Partial<TableSettings>) => void;
    hasCustomColumnWidths?: boolean;
    onResetColumnWidths?: () => void;
    /** When provided, shows a "Columns" tab with visibility + reorder */
    columns?: {
        rows: SettingsColumnDrawerRow[];
        onToggle: (key: string) => void;
        onReorderDrag: (fromKey: string, toKey: string) => void;
        onResetColumns?: () => void;
        /** Nested aggregation groups (e.g. Missed Opportunity Total/Avg/Median). */
        groups?: NestedColumnGroupDef[];
        /** Defaults shown immediately in the draft list when Reset Columns is clicked. */
        getDefaultRows?: () => SettingsColumnDrawerRow[];
    };
    /** Called on Save to persist draft column rows */
    onCommitColumns?: (rows: SettingsColumnDrawerRow[]) => void;
    /** Rendered at the top of the drawer body (above accordion / tabs). */
    headerContent?: ReactNode;
    extraTabs?: {
        key: string;
        label: string;
        content: ReactNode;
        footer?: (closeDrawer: () => void) => ReactNode;
    }[];
    hideColumnDividers?: boolean;
    /** Hide the built-in Display tab (e.g. for views where density/dividers don't apply) */
    hideDisplayTab?: boolean;
    triggerButtonProps?: Partial<IconButtonProps>;
    /** Renders sections as collapsible accordion panels instead of tabs (WidgetEditDrawer style) */
    useAccordionLayout?: boolean;
    /**
     * Apply setting/column changes to the live table immediately instead of buffering
     * them in draft state until Save. The Save button still closes the drawer, but the
     * underlying handlers already persist on every change so the table previews in realtime.
     */
    applyImmediately?: boolean;
    /** Badge count shown on the trigger button */
    badgeCount?: number;
    /** Called when the drawer opens (e.g. to snapshot state for discard). */
    onBeforeOpen?: () => void;
    /** Called when "Save Changes" is clicked (accordion layout footer). */
    onSaveChanges?: () => void;
    /** Called when "Discard Changes" is clicked (accordion layout footer). */
    onDiscardChanges?: () => void;
}

const DENSITY_OPTIONS = [
    { key: 'compact' as const, label: 'Compact', icon: TbLayoutList },
    { key: 'detailed' as const, label: 'Detailed', icon: TbListDetails },
];

const ReportingTableSettingsDrawer = ({
    settings,
    onUpdateSettings,
    hasCustomColumnWidths,
    onResetColumnWidths,
    columns,
    onCommitColumns,
    headerContent,
    extraTabs = [],
    hideColumnDividers,
    hideDisplayTab,
    triggerButtonProps,
    useAccordionLayout,
    badgeCount,
    onBeforeOpen,
    onSaveChanges,
    onDiscardChanges,
    applyImmediately,
}: ReportingTableSettingsDrawerProps) => {
    const { isOpen, onOpen, onClose } = useDisclosure();
    const [dragKey, setDragKey] = useState<string | null>(null);
    const [activeTab, setActiveTab] = useState(0);

    const [draftSettings, setDraftSettings] = useState<TableSettings>(settings);
    const [draftColumnRows, setDraftColumnRows] = useState<SettingsColumnDrawerRow[]>([]);
    const [pendingResetColumnWidths, setPendingResetColumnWidths] = useState(false);
    const [pendingResetColumns, setPendingResetColumns] = useState(false);

    const showColumnsTab = Boolean(columns && columns.rows.length > 0);
    const showTabbedLayout = showColumnsTab || extraTabs.length > 0;
    // deferLayout drives the tabbed/accordion chrome + Save footer.
    const deferLayout = Boolean(useAccordionLayout || showTabbedLayout);
    // deferApply drives whether changes buffer into draft state (commit on Save) or
    // apply live. applyImmediately opts into realtime previewing while keeping the
    // familiar Save button.
    const deferChanges = deferLayout && !applyImmediately;

    const initDrafts = useCallback(() => {
        setDraftSettings(settings);
        setDraftColumnRows(columns?.rows ? cloneNestedRows(columns.rows) : []);
        setPendingResetColumnWidths(false);
        setPendingResetColumns(false);
    }, [settings, columns?.rows]);

    useEffect(() => {
        if (isOpen && deferChanges) {
            initDrafts();
        }
    }, [isOpen, deferChanges, initDrafts]);

    const handleOpen = () => {
        onBeforeOpen?.();
        onOpen();
    };

    const handleSave = () => {
        if (deferChanges) {
            onUpdateSettings(draftSettings);
            if (pendingResetColumns && columns?.onResetColumns) {
                columns.onResetColumns();
            } else if (onCommitColumns && draftColumnRows.length > 0) {
                onCommitColumns(draftColumnRows);
            }
            if (pendingResetColumnWidths && onResetColumnWidths) {
                onResetColumnWidths();
            }
        }
        onSaveChanges?.();
        onClose();
    };

    const handleDiscard = () => {
        if (deferChanges) {
            initDrafts();
        }
        onDiscardChanges?.();
        onClose();
    };

    const effectiveSettings = deferChanges ? draftSettings : settings;
    const effectiveColumnRows = deferChanges ? draftColumnRows : (columns?.rows ?? []);

    const updateSettings = deferChanges
        ? (partial: Partial<TableSettings>) => setDraftSettings((prev) => ({ ...prev, ...partial }))
        : onUpdateSettings;

    const handleColumnToggle = (key: string) => {
        if (deferChanges) {
            setPendingResetColumns(false);
            setDraftColumnRows((prev) => {
                const hasNested = prev.some((r) => r.children && r.children.length > 0);
                if (hasNested) {
                    return toggleNestedRow(prev, key, columns?.groups ?? []);
                }
                return prev.map((r) => (r.key === key && !r.pinned ? { ...r, visible: !r.visible } : r));
            });
        } else {
            columns?.onToggle(key);
        }
    };

    const handleColumnReorder = (fromKey: string, toKey: string) => {
        if (deferChanges) {
            setPendingResetColumns(false);
            setDraftColumnRows((prev) => reorderColumnRows(prev, fromKey, toKey));
        } else {
            columns?.onReorderDrag(fromKey, toKey);
        }
    };

    const handleResetColumnsClick = () => {
        if (deferChanges) {
            if (columns?.getDefaultRows) {
                setDraftColumnRows(cloneNestedRows(columns.getDefaultRows()));
            }
            setPendingResetColumns(true);
        } else {
            columns?.onResetColumns?.();
        }
    };

    const handleResetColumnWidthsClick = () => {
        if (deferChanges) {
            setPendingResetColumnWidths(true);
        } else {
            onResetColumnWidths?.();
        }
    };

    const showResetColumnWidths = Boolean(
        onResetColumnWidths && (hasCustomColumnWidths || pendingResetColumnWidths)
    );
    const showResetColumns = Boolean(columns?.onResetColumns);

    const btnH = triggerButtonProps?.h ?? 8;
    const btnW = triggerButtonProps?.w ?? 8;
    const showBadge = badgeCount != null && badgeCount > 0;
    const triggerMinHWithBadge =
        typeof btnH === 'number' ? btnH + 6 : `calc(${btnH} + 6px)`;

    const iconButton = (
        <IconButton
            aria-label="Table Settings"
            icon={<YaxleIcon name="settings" size="sm" />}
            size="toolbar"
            variant="ghost"
            colorScheme="gray"
            color="fg.muted"
            onClick={handleOpen}
            {...triggerButtonProps}
        />
    );

    const saveFooter = deferLayout ? (
        <>
            {deferChanges && (
                <Button size="action" variant="outlineGray" onClick={handleDiscard}>
                    Cancel
                </Button>
            )}
            <Button size="action" bg="#0071E3" color="white" _hover={{ bg: '#3544a0' }} onClick={handleSave}>
                Save
            </Button>
        </>
    ) : undefined;

    return (
        <>
            {showBadge ? (
                <Box
                    display="flex"
                    flexDirection="column"
                    justifyContent="flex-end"
                    minH={triggerMinHWithBadge}
                    w={btnW}
                    flexShrink={0}
                >
                    <Box position="relative" w={btnW} h={btnH}>
                        {iconButton}
                        <Badge
                            position="absolute"
                            top="-6px"
                            right="-6px"
                            borderRadius="full"
                            colorScheme="blue"
                            fontSize="0.65rem"
                            minW="18px"
                            h="18px"
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                            px={1}
                        >
                            {badgeCount}
                        </Badge>
                    </Box>
                </Box>
            ) : (
                iconButton
            )}

            <AppDrawer
                isOpen={isOpen}
                onClose={deferLayout ? handleDiscard : onClose}
                title="Table Settings"
                size="md"
                footer={saveFooter}
                contentProps={{ display: 'flex', flexDirection: 'column' }}
                bodyProps={
                    useAccordionLayout || showTabbedLayout
                        ? { pt: 0, px: 0, pb: 0, display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden' }
                        : { pt: 4, pb: 8 }
                }
            >
                {useAccordionLayout ? (
                    <AccordionSettingsBody
                        headerContent={headerContent}
                        extraTabs={extraTabs}
                        showColumnsTab={showColumnsTab}
                        columnRows={effectiveColumnRows}
                        dragKey={dragKey}
                        setDragKey={setDragKey}
                        onColumnToggle={handleColumnToggle}
                        onColumnReorder={handleColumnReorder}
                        showResetColumns={showResetColumns}
                        onResetColumnsClick={handleResetColumnsClick}
                        pendingResetColumns={pendingResetColumns}
                        settings={effectiveSettings}
                        onUpdateSettings={updateSettings}
                        showResetColumnWidths={showResetColumnWidths}
                        onResetColumnWidthsClick={handleResetColumnWidthsClick}
                        pendingResetColumnWidths={pendingResetColumnWidths}
                        hideColumnDividers={hideColumnDividers}
                        hideDisplayTab={hideDisplayTab}
                    />
                ) : showTabbedLayout ? (
                    <TabbedSettingsBody
                        headerContent={headerContent}
                        activeTab={activeTab}
                        setActiveTab={setActiveTab}
                        extraTabs={extraTabs}
                        showColumnsTab={showColumnsTab}
                        columnRows={effectiveColumnRows}
                        dragKey={dragKey}
                        setDragKey={setDragKey}
                        onColumnToggle={handleColumnToggle}
                        onColumnReorder={handleColumnReorder}
                        showResetColumns={showResetColumns}
                        onResetColumnsClick={handleResetColumnsClick}
                        pendingResetColumns={pendingResetColumns}
                        settings={effectiveSettings}
                        onUpdateSettings={updateSettings}
                        showResetColumnWidths={showResetColumnWidths}
                        onResetColumnWidthsClick={handleResetColumnWidthsClick}
                        pendingResetColumnWidths={pendingResetColumnWidths}
                        onCloseDrawer={handleDiscard}
                        hideColumnDividers={hideColumnDividers}
                        hideDisplayTab={hideDisplayTab}
                    />
                ) : (
                    <DisplayOptionsPanel
                        settings={settings}
                        onUpdateSettings={onUpdateSettings}
                        hasCustomColumnWidths={hasCustomColumnWidths}
                        onResetColumnWidths={onResetColumnWidths}
                    />
                )}
            </AppDrawer>
        </>
    );
};

function TabbedSettingsBody({
    headerContent,
    activeTab,
    setActiveTab,
    extraTabs,
    showColumnsTab,
    columnRows,
    dragKey,
    setDragKey,
    onColumnToggle,
    onColumnReorder,
    showResetColumns,
    onResetColumnsClick,
    pendingResetColumns,
    settings,
    onUpdateSettings,
    showResetColumnWidths,
    onResetColumnWidthsClick,
    pendingResetColumnWidths,
    onCloseDrawer,
    hideColumnDividers,
    hideDisplayTab,
}: {
    headerContent?: ReactNode;
    activeTab: number;
    setActiveTab: (i: number) => void;
    extraTabs: ReportingTableSettingsDrawerProps['extraTabs'] & object[];
    showColumnsTab: boolean;
    columnRows: SettingsColumnDrawerRow[];
    dragKey: string | null;
    setDragKey: (key: string | null) => void;
    onColumnToggle: (key: string) => void;
    onColumnReorder: (fromKey: string, toKey: string) => void;
    showResetColumns: boolean;
    onResetColumnsClick: () => void;
    pendingResetColumns: boolean;
    settings: TableSettings;
    onUpdateSettings: (partial: Partial<TableSettings>) => void;
    showResetColumnWidths: boolean;
    onResetColumnWidthsClick: () => void;
    pendingResetColumnWidths: boolean;
    onCloseDrawer: () => void;
    hideColumnDividers?: boolean;
    hideDisplayTab?: boolean;
}) {
    const allTabs = [
        ...extraTabs.map((t) => t.label),
        ...(showColumnsTab ? ['Columns'] : []),
        ...(hideDisplayTab ? [] : ['Display']),
    ];

    useEffect(() => {
        if (activeTab > allTabs.length - 1) {
            setActiveTab(Math.max(0, allTabs.length - 1));
        }
    }, [activeTab, allTabs.length, setActiveTab]);

    return (
        <>
            {headerContent && (
                <Box flexShrink={0} px={6} pt={4} pb={2}>
                    {headerContent}
                </Box>
            )}
            <Flex flexShrink={0} borderBottom="1.5px solid" borderColor="gray.300" px={6}>
                {allTabs.map((label, i) => (
                    <Box
                        key={label}
                        as="button"
                        type="button"
                        onClick={() => setActiveTab(i)}
                        fontWeight="medium"
                        fontSize="sm"
                        color={activeTab === i ? 'gray.800' : 'gray.500'}
                        bg="transparent"
                        border="none"
                        borderBottom="1.5px solid"
                        borderColor={activeTab === i ? 'gray.500' : 'transparent'}
                        mb="-1.5px"
                        py={2}
                        px={3}
                        cursor="pointer"
                        transition="all 0.15s"
                        _hover={{
                            color: activeTab === i ? 'gray.800' : 'gray.700',
                        }}
                    >
                        {label}
                    </Box>
                ))}
            </Flex>

            <Box flex={1} overflowY="auto" px={6} pt={5} pb={8}>
                {extraTabs.map((tab, i) => (
                    <Box key={tab.key} display={activeTab === i ? 'block' : 'none'}>
                        {tab.content}
                    </Box>
                ))}
                {showColumnsTab && (
                    <Box display={activeTab === extraTabs.length ? 'block' : 'none'}>
                        <SettingsColumnsPanel
                            columnRows={columnRows}
                            dragKey={dragKey}
                            setDragKey={setDragKey}
                            onColumnToggle={onColumnToggle}
                            onColumnReorder={onColumnReorder}
                            showResetColumns={showResetColumns}
                            onResetColumnsClick={onResetColumnsClick}
                            pendingResetColumns={pendingResetColumns}
                        />
                    </Box>
                )}
                {!hideDisplayTab && (
                    <Box display={activeTab === extraTabs.length + (showColumnsTab ? 1 : 0) ? 'block' : 'none'}>
                        <DisplayOptionsPanel
                            settings={settings}
                            onUpdateSettings={onUpdateSettings}
                            showResetColumnWidths={showResetColumnWidths}
                            onResetColumnWidthsClick={onResetColumnWidthsClick}
                            pendingResetColumnWidths={pendingResetColumnWidths}
                            hideColumnDividers={hideColumnDividers}
                        />
                    </Box>
                )}
            </Box>
            {activeTab < extraTabs.length && extraTabs[activeTab]?.footer && (
                <Box flexShrink={0} borderTopWidth="1px" borderColor="gray.300" px={6} py={4}>
                    {extraTabs[activeTab].footer!(onCloseDrawer)}
                </Box>
            )}
        </>
    );
}

function DisplayOptionsPanel({
    settings,
    onUpdateSettings,
    showResetColumnWidths,
    onResetColumnWidthsClick,
    pendingResetColumnWidths,
    hideColumnDividers,
    hasCustomColumnWidths,
    onResetColumnWidths,
}: {
    settings: TableSettings;
    onUpdateSettings: (partial: Partial<TableSettings>) => void;
    showResetColumnWidths?: boolean;
    onResetColumnWidthsClick?: () => void;
    pendingResetColumnWidths?: boolean;
    hideColumnDividers?: boolean;
    hasCustomColumnWidths?: boolean;
    onResetColumnWidths?: () => void;
}) {
    const showReset = showResetColumnWidths ?? Boolean(hasCustomColumnWidths && onResetColumnWidths);
    const onResetClick = onResetColumnWidthsClick ?? onResetColumnWidths;

    return (
        <>
            <Box mb={6}>
                <Text fontSize="md" fontWeight="bold" color="gray.900" mb={4}>
                    Table Density
                </Text>
                <VStack spacing={3} align="stretch">
                    {DENSITY_OPTIONS.map((option) => {
                        const isSelected = settings.density === option.key;
                        return (
                            <Box
                                key={option.key}
                                as="button"
                                type="button"
                                onClick={() => onUpdateSettings({ density: option.key })}
                                border="1px solid"
                                borderColor={isSelected ? '#0071E3' : 'gray.200'}
                                borderRadius="lg"
                                px={4}
                                py={3}
                                cursor="pointer"
                                bg={isSelected ? '#E6F1FD' : 'white'}
                                _hover={{
                                    borderColor: isSelected ? '#0071E3' : 'gray.300',
                                    bg: isSelected ? '#E6F1FD' : 'gray.50',
                                }}
                                transition="all 0.15s"
                                textAlign="left"
                                w="100%"
                            >
                                <HStack spacing={3}>
                                    <Flex
                                        align="center"
                                        justify="center"
                                        w="20px"
                                        h="20px"
                                        borderRadius="full"
                                        border="2px solid"
                                        borderColor={isSelected ? '#0071E3' : 'gray.300'}
                                        flexShrink={0}
                                    >
                                        {isSelected && (
                                            <Box w="10px" h="10px" borderRadius="full" bg="#0071E3" />
                                        )}
                                    </Flex>
                                    <Icon
                                        as={option.icon as React.ElementType}
                                        boxSize={5}
                                        color={isSelected ? '#0071E3' : 'gray.500'}
                                    />
                                    <Text
                                        fontSize="sm"
                                        fontWeight="medium"
                                        color={isSelected ? '#0071E3' : 'gray.700'}
                                    >
                                        {option.label}
                                    </Text>
                                </HStack>
                            </Box>
                        );
                    })}
                </VStack>
            </Box>

            {!hideColumnDividers && (
                <>
                    <Divider mb={6} />

                    <Flex
                        align="center"
                        justify="space-between"
                        cursor="pointer"
                        onClick={() => onUpdateSettings({ columnDividers: !settings.columnDividers })}
                        mb={showReset ? 0 : undefined}
                    >
                        <HStack spacing={3}>
                            <Icon as={TbColumns as React.ElementType} boxSize={5} color="gray.500" />
                            <Box>
                                <Text fontSize="sm" fontWeight="semibold" color="gray.800">
                                    Column Dividers
                                </Text>
                                <Text fontSize="xs" color="gray.500">
                                    Display vertical dividers between each column
                                </Text>
                            </Box>
                        </HStack>
                        <Switch
                            isChecked={settings.columnDividers}
                            pointerEvents="none"
                            colorScheme="blue"
                            size="md"
                        />
                    </Flex>
                </>
            )}

            {showReset && onResetClick && (
                <>
                    <Divider my={6} />
                    <Flex
                        align="center"
                        justify="space-between"
                        cursor="pointer"
                        onClick={onResetClick}
                        _hover={{ bg: 'gray.50' }}
                        borderRadius="md"
                        p={2}
                        mx={-2}
                        bg={pendingResetColumnWidths ? 'blue.50' : undefined}
                    >
                        <HStack spacing={3}>
                            <Icon as={TbArrowBackUp as React.ElementType} boxSize={5} color="gray.500" />
                            <Box>
                                <Text fontSize="sm" fontWeight="semibold" color="gray.800">
                                    Reset Column Widths
                                </Text>
                                <Text fontSize="xs" color="gray.500">
                                    Restore all columns to their default widths
                                </Text>
                            </Box>
                        </HStack>
                    </Flex>
                </>
            )}
        </>
    );
}

function AccordionSettingsBody({
    headerContent,
    extraTabs,
    showColumnsTab,
    columnRows,
    dragKey,
    setDragKey,
    onColumnToggle,
    onColumnReorder,
    showResetColumns,
    onResetColumnsClick,
    pendingResetColumns,
    settings,
    onUpdateSettings,
    showResetColumnWidths,
    onResetColumnWidthsClick,
    pendingResetColumnWidths,
    hideColumnDividers,
    hideDisplayTab,
}: {
    headerContent?: ReactNode;
    extraTabs: ReportingTableSettingsDrawerProps['extraTabs'] & object[];
    showColumnsTab: boolean;
    columnRows: SettingsColumnDrawerRow[];
    dragKey: string | null;
    setDragKey: (key: string | null) => void;
    onColumnToggle: (key: string) => void;
    onColumnReorder: (fromKey: string, toKey: string) => void;
    showResetColumns: boolean;
    onResetColumnsClick: () => void;
    pendingResetColumns: boolean;
    settings: TableSettings;
    onUpdateSettings: (partial: Partial<TableSettings>) => void;
    showResetColumnWidths: boolean;
    onResetColumnWidthsClick: () => void;
    pendingResetColumnWidths: boolean;
    hideColumnDividers?: boolean;
    hideDisplayTab?: boolean;
}) {
    const sectionCount =
        extraTabs.length + (showColumnsTab ? 1 : 0) + (hideDisplayTab ? 0 : 1);
    const defaultIndices = Array.from({ length: sectionCount }, (_, i) => i);

    return (
        <Box pt={4} px={6} pb={8} overflowY="auto" flex={1}>
            {headerContent && <Box mb={4}>{headerContent}</Box>}
            <Accordion allowMultiple defaultIndex={defaultIndices}>
                {extraTabs.map((tab) => (
                    <Box
                        key={tab.key}
                        border="1px solid"
                        borderColor="gray.200"
                        borderRadius="lg"
                        overflow="visible"
                        mb={3}
                    >
                        <AccordionItem border="none">
                            <AccordionButton px={4} py={3} _hover={{ bg: 'transparent' }}>
                                <Text flex={1} textAlign="left" fontWeight="semibold" fontSize="md" color="gray.800">
                                    {tab.label}
                                </Text>
                                <AccordionIcon />
                            </AccordionButton>
                            <AccordionPanel px={4} pb={4} pt={1}>
                                {tab.content}
                            </AccordionPanel>
                        </AccordionItem>
                    </Box>
                ))}

                {showColumnsTab && (
                    <Box
                        border="1px solid"
                        borderColor="gray.200"
                        borderRadius="lg"
                        overflow="visible"
                        mb={3}
                    >
                        <AccordionItem border="none">
                            <AccordionButton px={4} py={3} _hover={{ bg: 'transparent' }}>
                                <Text flex={1} textAlign="left" fontWeight="semibold" fontSize="md" color="gray.800">
                                    Columns
                                </Text>
                                <AccordionIcon />
                            </AccordionButton>
                            <AccordionPanel px={4} pb={4} pt={1}>
                                <SettingsColumnsPanel
                                    columnRows={columnRows}
                                    dragKey={dragKey}
                                    setDragKey={setDragKey}
                                    onColumnToggle={onColumnToggle}
                                    onColumnReorder={onColumnReorder}
                                    showResetColumns={showResetColumns}
                                    onResetColumnsClick={onResetColumnsClick}
                                    pendingResetColumns={pendingResetColumns}
                                />
                            </AccordionPanel>
                        </AccordionItem>
                    </Box>
                )}

                {!hideDisplayTab && (
                    <Box
                        border="1px solid"
                        borderColor="gray.200"
                        borderRadius="lg"
                        overflow="visible"
                    >
                        <AccordionItem border="none">
                            <AccordionButton px={4} py={3} _hover={{ bg: 'transparent' }}>
                                <Text flex={1} textAlign="left" fontWeight="semibold" fontSize="md" color="gray.800">
                                    Display
                                </Text>
                                <AccordionIcon />
                            </AccordionButton>
                            <AccordionPanel px={4} pb={4} pt={1}>
                                <DisplayOptionsPanel
                                    settings={settings}
                                    onUpdateSettings={onUpdateSettings}
                                    showResetColumnWidths={showResetColumnWidths}
                                    onResetColumnWidthsClick={onResetColumnWidthsClick}
                                    pendingResetColumnWidths={pendingResetColumnWidths}
                                    hideColumnDividers={hideColumnDividers}
                                />
                            </AccordionPanel>
                        </AccordionItem>
                    </Box>
                )}
            </Accordion>
        </Box>
    );
}

export default ReportingTableSettingsDrawer;
